import { Form } from "@inertiajs/react"
import { Send } from "lucide-react"
import { forwardRef, useState } from "react"
import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

/**
 * OKS-37 — Inertia <Form> + plain shadcn primitives composer.
 *
 * **CREW HARD RULES:**
 *   - Inertia `<Form>` (not shadcn `Form`/`FormField`/`FormItem`).
 *   - No react-hook-form, no zod.
 *   - Errors rendered from the render-prop `errors` object.
 *
 * Behaviour:
 *   - Cmd/Ctrl+Enter submits.
 *   - Char counter visible past 3500 (PRD §3.7).
 *   - Send disabled when length > limit OR processing.
 *   - On success the textarea is cleared (`resetOnSuccess`); on
 *     error the textarea retains its content + Inertia errors object
 *     surfaces the validation message.
 */
interface MessageComposerProps {
  /** POST URL — `crewMessageMessagesPath(id)` (brand) or `messagesPath()` (creator). */
  actionUrl: string
  /** Server-emitted limit (mirrors `Message::MAX_BODY_LENGTH`). */
  limit: number
  /** When non-null, used in the textarea aria-label for AT context. */
  conversationId: number | null
  /**
   * OKS-724 — invoked after a SUCCESSFUL send only (wired to Inertia
   * `<Form onSuccess>`, which fires on 2xx). Call sites use it to force the
   * thread to the newest message (`MessageThreadScrollerHandle#scrollToNewest`).
   * A failed send routes through `onError`, so this never runs on failure —
   * no scroll, draft retained.
   */
  onSuccess?: () => void
}

export const MessageComposer = forwardRef<
  HTMLTextAreaElement,
  MessageComposerProps
>(function MessageComposer(
  { actionUrl, limit, conversationId, onSuccess },
  ref,
) {
  const { t } = useTranslation()
  // Local state for length-based disable + counter visibility. The
  // Inertia <Form> handles submission state — we just feed it the
  // textarea value via name="message[body]" so the controller's
  // strong-params lift it cleanly.
  const [length, setLength] = useState(0)
  const remaining = limit - length
  const showCounter = length > 3500
  const overLimit = length > limit

  return (
    <Form
      method="post"
      action={actionUrl}
      options={{ preserveScroll: true, preserveState: true }}
      // OKS-724 — fires on 2xx only; the parent forces the thread to the
      // newest message here. Failed sends route through `onError`, so this
      // never runs on failure (no scroll, draft retained).
      onSuccess={onSuccess}
      resetOnSuccess
      // OKS-723 — `shrink-0`: the composer is the non-shrinking footer band of
      // the messaging flex column (creator `Creator/Messages/Index` + brand
      // `ThreadPane`). Without it, a tight column could shrink the composer
      // below its content and let it scroll off; only the thread region
      // (`min-h-0 flex-1`) may absorb overflow. Keeps the composer anchored
      // and visible on entry.
      // OKS-874 — `max-md:py-2` here + `max-md:gap-1.5` on the column below
      // reclaim 10px of composer height under `md`. The 44px tap-target floor
      // grew the Send button 36→44, and this footer is `shrink-0`, so those
      // 8px came straight out of its `flex-1 min-h-0` sibling — the message
      // list — which at 375×568 had a measured band of 8px and NO SLACK AT
      // ALL, so it went to 0 and rendered `hidden`.
      //
      // That distinction is load-bearing: the margin was zero, not eight
      // pixels. A longer FR string, a two-line hint, or a user font bump each
      // collapse this column independently. Do not read the 8px below as a
      // buffer that 8px of growth happened to exhaust.
      //
      // Measured partition at 375×568 (`offsetHeight`, Chromium):
      //   before      page 186 = header 65 + composer 121 + band  0  ← hidden
      //   after       page 186 = header 65 + composer 111 + band 10
      //   pre-ticket  page 186 = header 65 + composer 113 + band  8
      // Page-scroll delta stayed 0, so the band's 10px was not bought by
      // pushing the column past the viewport.
      //
      // WHY 10 AND NOT THE 8 THAT WOULD MERELY RESTORE PARITY — a design
      // reason, and deliberately not a testing one. `p-3` + `gap-2` was always
      // desktop padding surviving into a phone layout; tightening it under
      // `md` is a change worth making on its own, independent of any spec.
      // There is also no budget being spent: reclaiming MORE from the composer
      // gives the band MORE, so this does not narrow what a follow-up can do.
      //
      // ⚠️ The extra 2px is explicitly NOT load-bearing for the band guard in
      // `messaging-creator-composer-viewport.spec.ts`. That guard sizes its
      // tolerance as though the band were 8px, by contract — instrument
      // robustness is the instrument's job. Do not treat this padding as the
      // reason a pinned threshold passes, and do not tighten it further to buy
      // a test more headroom.
      //
      // `max-md:`-scoped so ≥768px emits no rule at all — AC3 holds by
      // absence of declaration, not by a reset that could be forgotten.
      // Verified at 1280×800 rather than assumed from the prefix: composer
      // 113 and Send 36, i.e. neither the floor NOR this reclaim reaches
      // desktop.
      //
      // ⚠️ This is a NO-REGRESSION reclaim, not a repair. The band was 8px
      // BEFORE this ticket and that is already unusable — OKS-723's spec
      // passed anyway because Playwright counts 8px as visible. The
      // pre-existing collapse (and `thread-pane.tsx`, same shape on the
      // brand side) is tracked separately; do not read this comment as a
      // claim that the messaging column is now correct at this viewport.
      className="border-border bg-background shrink-0 border-t p-3 max-md:py-2"
      data-testid="messaging-composer-form"
    >
      {({ processing, errors }) => {
        // OKS-945 lever A — under `md` this inner column lays out as a WRAPPING
        // ROW, so the textarea and Send share one line. That reclaims the 44px
        // control row plus its 6px gap: 50px of the 90px this ticket takes
        // back, and by far the largest single lever.
        //
        // The partition it comes out of was MEASURED, not derived (CI-bookworm,
        // run 30626307866 — and it reports the same 10px band darwin recorded
        // on OKS-874, so header and composer reproduce to the pixel on the
        // platform that judges this):
        //
        //   composer 111 = border-t 1 + py-2 16 + textarea 44 + gap-1.5 6
        //                                                     + control row 44
        //
        // Both terms this lever removes are metric-INDEPENDENT tokens (the gap,
        // and the Send button's `max-md:min-h-11` floor from OKS-874), so the
        // 50 is subtraction FROM a measurement rather than arithmetic over one.
        // That is not true of every lever in this ticket and the difference is
        // worth keeping straight: the header's 24px (OKS-945 levers C + C') is
        // token arithmetic against a total that was measured but never
        // partitioned, and the tab-bar reserve's 16px rests on a box-model
        // assumption that can return 0 rather than being off by a pixel.
        //
        // MECHANISM — `max-md:contents` on the control row below. Send is
        // NESTED inside that row, not a sibling of the textarea, and `order` /
        // `flex-direction` only reorder items WITHIN a single flex container —
        // so no amount of reordering can produce the inline shape, and the two
        // obvious forces each break a contract (duplicating the node breaks the
        // single `messaging-composer-send` testid; hoisting it in the DOM
        // changes `md+`). `display: contents` erases the row's own box while
        // keeping its children, promoting the meta div and Send to direct items
        // of this column. ONE Send node, and `md+` is unchanged by ABSENCE of
        // declaration rather than by a reset that could be forgotten (OKS-874
        // AC3 precedent). The wrapper is a semantics-free <div>, so
        // `display:contents` carries none of its a11y hazards — those attach to
        // tables, lists and buttons.
        //
        // ⚠️ DELIBERATE DEVIATION from BRIEF-OKS-945 §6.1, recorded here
        // because that document is binding. §6.1 specifies that the meta row
        // "renders only when it has content", and patches the `md+` fallout
        // with `justify-end`. That mechanism DELETES the shortcut hint at
        // 640–767px, where it is `hidden sm:inline` and is supposed to show. So
        // the row is always RENDERED and merely hidden below `sm` when empty:
        // identical at the binding 375px viewport (a `display:none` flex item
        // contributes no box and emits no gap), the hint survives at 640–767,
        // and `flex-1` is still present at `md+` — which means AC10's "Send
        // jumps left on an idle desktop composer" hazard cannot arise at all,
        // rather than arising and being patched after the fact.
        const hasMetaContent = showCounter || overLimit || Boolean(errors.body)

        return (
          <div className="flex flex-col gap-2 max-md:flex-row max-md:flex-wrap max-md:items-end max-md:gap-1.5">
            <Textarea
              ref={ref}
              name="message[body]"
              rows={2}
              maxLength={
                limit + 100
              } /* hard-cap UI input slightly above limit so counter still shows */
              onChange={(e) => setLength(e.currentTarget.value.length)}
              onKeyDown={(e) => {
                if (
                  (e.metaKey || e.ctrlKey) &&
                  e.key === "Enter" &&
                  !overLimit &&
                  !processing
                ) {
                  e.preventDefault()
                  // Trigger the closest form's submit — Inertia's <Form>
                  // intercepts the native submit event.
                  e.currentTarget.form?.requestSubmit()
                }
              }}
              placeholder={t("messaging.composer.placeholder")}
              aria-label={
                conversationId !== null
                  ? `Composer for conversation ${conversationId}`
                  : t("messaging.composer.placeholder")
              }
              data-testid="messaging-composer-textarea"
              /*
                OKS-945 lever E — cap the composer at TWO lines under `md`,
                scrolling internally past that. Not a reclaim: this is the lever
                that makes every other number HOLD rather than merely be true
                once.

                `field-sizing-content` on the shared primitive means this
                textarea grows with every typed line and nothing caps it, while
                the composer is `shrink-0` and the message band is the only
                `flex: 1 1 0%` child — so the band pays 1:1 for every pixel the
                textarea gains. Measured on the pre-fix tree (run 30680448579):
                idle band 10px, band with two lines typed 0px, on BOTH reachable
                branches. That is a live, user-triggerable collapse, and it is
                why reclaim alone would have produced a column that is correct
                only until the creator does the one thing the screen exists for.

                66px = 2 x 24px line box + px-3/py-2 16 + borders 2.

                `overflow-y-auto` is EXPLICIT rather than left to the browser
                default, because `maxLength` is deliberately `limit + 100` so
                the counter still shows past the limit — a cap that clipped
                instead of scrolling would hide the very text the user is being
                told to trim. Native textarea scrolling follows the caret, so
                the creator always sees what they are typing.

                🔴 The cap lives HERE, on the instance, and never on
                `ui/textarea.tsx`. `field-sizing-content` lives on the shared
                primitive, which makes the primitive look like the natural home
                for a `max-h`; it is a trap twice over — an un-ledgered hand-edit
                to `components/ui/**` is a 95-severity merge block, and it would
                cap every textarea in Crew, an app-wide change nobody asked for
                and no spec here measures. The `min-h-[44px]` on this same
                instance is the precedent that makes an instance-level cap
                legitimate rather than a workaround. (Two consequences of that
                override, so a later reader doesn't think the partition is
                wrong: this textarea measures 44 and not the primitive's 64
                `min-h-16`, and `rows={2}` above is present but INERT —
                `field-sizing-content` sizes to content and overrides it.)

                ⚠️ A GREEN CI RUN DOES NOT ESTABLISH THAT THIS CAP BINDS. At two
                typed lines the textarea measures 66px with or WITHOUT it — the
                cap only engages at 3+ lines — so the AC2 typed legs red on the
                pre-fix tree entirely on the missing RECLAIM, and would stay
                green if this token were deleted tomorrow. The only guard that
                can see it is AC13's 5-line leg. Contrast lever A above, which
                is self-catching: a `display:contents` that silently no-ops
                surfaces as a 50px shortfall in the measured idle band.
              */
              className="min-h-[44px] max-md:max-h-[66px] max-md:min-w-0 max-md:flex-1 max-md:overflow-y-auto"
            />
            {/*
              `max-md:contents` — see lever A above. Erasing this row's box
              below `md` is what puts Send inline with the textarea; at `md+`
              the declaration does not apply and this is an ordinary
              `justify-between` flex row, unchanged.
            */}
            <div className="flex items-center justify-between gap-2 max-md:contents">
              <div
                className={cn(
                  "flex flex-1 items-center gap-2 max-md:order-last max-md:w-full",
                  // Hidden rather than unrendered, and below `sm` rather than
                  // below `md` — see the deviation note on lever A. `w-full` +
                  // `order-last` put this on its own line UNDER the inline
                  // textarea+Send row when it does have content.
                  !hasMetaContent && "max-sm:hidden",
                )}
                data-testid="messaging-composer-meta"
              >
                {showCounter && (
                  <span
                    className={cn(
                      "text-xs",
                      overLimit ? "text-destructive" : "text-muted-foreground",
                    )}
                    data-testid="messaging-composer-counter"
                  >
                    {t("messaging.composer.char_counter", {
                      remaining: Math.max(remaining, 0),
                    })}
                  </span>
                )}
                {/*
                    Client-side length-error label — shown the moment the
                    user types past `limit`, BEFORE submission. Send is
                    also disabled (see Button below). Server-side AR
                    validation (rendered via `errors.body` below) is the
                    hard gate; this is the proactive UX hint.
                  */}
                {overLimit && !errors.body && (
                  <span
                    className="text-destructive text-xs"
                    data-testid="messaging-composer-length-error"
                  >
                    {t("messaging.composer.length_error")}
                  </span>
                )}
                {errors.body && (
                  <InputError
                    messages={
                      Array.isArray(errors.body)
                        ? errors.body
                        : [String(errors.body)]
                    }
                    data-testid="messaging-composer-error"
                  />
                )}
                {/*
                    Subtle shortcut hint at ≥640px — never blocks layout
                    on mobile. UX V2 §Microcopy.

                    OKS-945 — this hint is why the wrapper above is HIDDEN
                    when empty rather than unrendered: at 640–767px it is
                    visible and the wrapper must still exist to hold it.
                  */}
                <span
                  className="text-muted-foreground hidden text-xs sm:inline"
                  data-testid="messaging-composer-shortcut-hint"
                >
                  {t("messaging.composer.shortcut_hint")}
                </span>
              </div>
              <Button
                type="submit"
                disabled={processing || overLimit || length === 0}
                aria-busy={processing}
                aria-label={t("messaging.composer.send_aria")}
                data-testid="messaging-composer-send"
                // OKS-945 lever A — inline with the textarea below `md`, so it
                // must not absorb the shrink that the `flex-1` textarea is there
                // to take. `max-md:`-scoped: at `md+` this row is unchanged and
                // Send never shrinks there anyway.
                className="max-md:shrink-0"
              >
                {processing ? (
                  <Spinner className="size-4" />
                ) : (
                  <Send className="size-4" aria-hidden="true" />
                )}
                <span className="sr-only sm:not-sr-only sm:ml-1">
                  {t("messaging.composer.send")}
                </span>
              </Button>
            </div>
          </div>
        )
      }}
    </Form>
  )
})
